import * as React from "react";
import { useCallback, useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import * as Immutable from "immutable";
import { useTranslation } from "react-i18next";
import LogViewWidget from "logview/components/LogViewWidget";
import useOnBrowserNavigation from "data-lake/hooks/useOnBrowserNavigation";
import TypeSpecificValue from "views/components/TypeSpecificValue";
import CustomHighlighting from "views/components/highlighting/CustomHighlighting";
import { PAGE_SIZE, MAX_LOADED_MESSAGES, INITIAL_PAGE, MAX_PAGE } from "data-lake/preview/Constants";
import BulkActions from "data-lake/preview/BulkActions";
import { Alert } from "components/bootstrap";
import { pollJob } from "views/logic/slices/executeJobResult";
import formatBackendMessages from "data-lake/preview/utils/formatBackendMessages";
import DLLogViewWidgetContainer from "data-lake/preview/DLLogViewWidgetContainer";
import { useSendWidgetConfigUpdateTelemetry } from "views/components/widgets/telemetry";
import stopSearchResultsPolling from "data-lake/preview/utils/stopSearchResultsPolling";
import usePermissions from "hooks/usePermissions";
import UserNotification from "util/UserNotification";
import PreviewIntro from "data-lake/preview/PreviewIntro";
import usePreviewLayout from "data-lake/preview/hooks/usePreviewLayout";
import useFieldTypes from "data-lake/hooks/useFieldTypes";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
const MessageLimitAlert = styled(Alert)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.sm};
  `
);
const initialLoadedAllMessages = (initialPageTotal, messages) => initialPageTotal < PAGE_SIZE || initialPageTotal > PAGE_SIZE && initialPageTotal === messages.length;
const ResetListState = ({
  resetListState,
  messages,
  initialPageTotal,
  setLoadedAllMessages
}) => {
  const prevMessages = useRef(messages);
  const { setSelectedEntities } = useSelectedEntities();
  useEffect(() => {
    if (prevMessages && prevMessages.current !== messages) {
      setLoadedAllMessages(initialLoadedAllMessages(initialPageTotal, messages));
      setSelectedEntities([]);
      resetListState();
      prevMessages.current = messages;
    }
  }, [initialPageTotal, messages, resetListState, setLoadedAllMessages, setSelectedEntities]);
  useOnBrowserNavigation(resetListState);
  return null;
};
const Visualisation = ({
  messages,
  widget,
  editing,
  mostRecentJobIds,
  onChangeConfig,
  initialPageTotal,
  setLoading,
  isFetchingFirstPage = void 0,
  hasExecutedSearch = void 0
}) => {
  const { t } = useTranslation("enterprise_data_lake");
  const { data: fieldTypes } = useFieldTypes();
  const { isPermitted } = usePermissions();
  const [loadedAllMessages, setLoadedAllMessages] = useState(initialLoadedAllMessages(initialPageTotal, messages));
  const { defaultFields, timestampField } = usePreviewLayout();
  const onLoadMessages = useCallback(
    async (after) => {
      const nextPage = after.page + 1;
      if (after.page * after.perPage >= MAX_LOADED_MESSAGES) {
        setLoadedAllMessages(true);
        return Promise.resolve({ messages: [], after });
      }
      const result = await pollJob({
        stopPolling: (progress) => stopSearchResultsPolling(nextPage, progress),
        jobIds: {
          nodeId: mostRecentJobIds.nodeId,
          asyncSearchId: mostRecentJobIds.asyncSearchId
        },
        result: null,
        page: nextPage,
        perPage: PAGE_SIZE
      }).catch((error) => UserNotification.error(t("widget.load_failed", { message: error.message })));
      if (!result) {
        return Promise.resolve({ messages: [], after });
      }
      const queryId = Object.keys(result.results)[0];
      const searchTypeId = Object.keys(result.results[queryId].search_types)[0];
      const { messages: newMessages } = result.results[queryId].search_types[searchTypeId];
      if (newMessages.length === 0) {
        setLoadedAllMessages(true);
      }
      return Promise.resolve({
        messages: formatBackendMessages(newMessages),
        after: { page: nextPage, perPage: PAGE_SIZE }
      });
    },
    [mostRecentJobIds == null ? void 0 : mostRecentJobIds.asyncSearchId, mostRecentJobIds == null ? void 0 : mostRecentJobIds.nodeId, t]
  );
  const onLoadMessage = useCallback((message) => {
    const filteredFields = Object.fromEntries(
      Object.entries(message.message).filter(([_, value]) => value !== null && value !== void 0)
    );
    return Promise.resolve({
      formatted_fields: filteredFields,
      filtered_fields: filteredFields,
      fields: filteredFields
    });
  }, []);
  const renderFieldValue = useCallback(
    (field, fieldType, value) => /* @__PURE__ */ React.createElement(CustomHighlighting, { field, value }, /* @__PURE__ */ React.createElement(TypeSpecificValue, { field, value, type: fieldType })),
    []
  );
  const bottomSection = useCallback(
    (loadedMessagesCount, bottomPageId) => {
      if (loadedMessagesCount !== MAX_LOADED_MESSAGES || bottomPageId !== MAX_LOADED_MESSAGES / PAGE_SIZE) {
        return null;
      }
      return /* @__PURE__ */ React.createElement(MessageLimitAlert, { bsStyle: "warning", className: "no-bm" }, t("widget.message_limit", { max: MAX_LOADED_MESSAGES }));
    },
    [t]
  );
  const bulkSelection = isPermitted("data_lake_archive:restore") ? {
    actions: /* @__PURE__ */ React.createElement(BulkActions, { retrievalPayload: { sourceId: widget.streams[0] } })
  } : void 0;
  return /* @__PURE__ */ React.createElement(
    LogViewWidget,
    {
      timestampField,
      isFetchingFirstPage,
      defaultFields,
      config: widget.config,
      bottomSection,
      initialAfter: { page: INITIAL_PAGE, perPage: PAGE_SIZE },
      infiniteScrollDirection: "DOWN",
      messages,
      onLoadMessages,
      renderFieldValue,
      editing,
      loadedAllMessages: (_loadedMessagesCount, _after, bottomPageId) => loadedAllMessages || bottomPageId === MAX_PAGE,
      onLoadMessage,
      setLoadingState: setLoading,
      fields: Immutable.List(fieldTypes),
      onChangeConfig: (config) => onChangeConfig(config),
      bulkSelection,
      placeholder: !hasExecutedSearch && !isFetchingFirstPage && /* @__PURE__ */ React.createElement(PreviewIntro, null),
      hasExecutedSearch
    },
    ({ resetListState }) => /* @__PURE__ */ React.createElement(
      ResetListState,
      {
        resetListState,
        messages,
        setLoadedAllMessages,
        initialPageTotal
      }
    )
  );
};
const DLLogViewWidget = ({
  editing,
  effectiveTimerange,
  initialPageTotal,
  isFetchingFirstPage,
  messages,
  mostRecentJobIds,
  onChangeWidget,
  onToggleEdit,
  widget,
  hasExecutedSearch
}) => {
  const sendWidgetConfigUpdateTelemetry = useSendWidgetConfigUpdateTelemetry();
  const [loading, setLoading] = useState(false);
  const onChangeConfig = useCallback(
    (newConfig) => {
      sendWidgetConfigUpdateTelemetry();
      return onChangeWidget(widget.toBuilder().config(newConfig).build());
    },
    [onChangeWidget, sendWidgetConfigUpdateTelemetry, widget]
  );
  return /* @__PURE__ */ React.createElement(
    DLLogViewWidgetContainer,
    {
      hasExecutedSearch,
      widget,
      editing,
      isFetchingFirstPage,
      loading,
      onToggleEdit,
      effectiveTimerange,
      onChangeConfig,
      onChangeWidget
    },
    /* @__PURE__ */ React.createElement(
      Visualisation,
      {
        hasExecutedSearch,
        isFetchingFirstPage,
        setLoading,
        editing,
        messages,
        onChangeConfig,
        widget,
        mostRecentJobIds,
        initialPageTotal
      }
    )
  );
};
export default DLLogViewWidget;
